/* global prefersReducedMotion, getTrafficOrigin */

const DEMO_CONSENT_MESSAGE = 'Não é possível enviar os dados sem aceitar a Política de Privacidade.';

/**
 * Posts the demo request to the form's action (`enviar-demonstracao`, which forwards the lead to
 * the BRSIS contacts API) and resolves only when the server confirms it.
 * @param {HTMLFormElement} form The demo request form.
 * @returns {Promise<void>} Resolves when the request was accepted; rejects with an Error whose
 * message can be shown to the user.
 */
function sendDemoRequest(form) {
    const fallback = 'Não foi possível enviar agora. Tente novamente em instantes.';

    return fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
    })
        .catch(() => {
            throw new Error(fallback);
        })
        .then(response => response.json()
            .catch(() => ({}))
            .then(body => {
                if (!response.ok || !body.success) {
                    throw new Error(body.message || fallback);
                }
            }));
}

/**
 * Copies the visitor's traffic origin (UTMs, ad click ids, referrer — see getTrafficOrigin() in
 * global.js) into the form's hidden fields, so the server can tell Google Ads, social networks and
 * other sources apart.
 * @param {HTMLFormElement} form The demo request form.
 * @returns {void}
 */
function fillTrafficOrigin(form) {
    const origin = getTrafficOrigin();

    Object.keys(origin).forEach(name => {
        const field = form.elements.namedItem(name);
        if (field !== null && field.type === 'hidden') {
            field.value = origin[name];
        }
    });
}

/**
 * Wires the demo request form (`#form-demo`, validated by Bouncer): shows a summary message when
 * validation fails (a dedicated one when only the Privacy Policy consent is missing) and, once it
 * passes, sends the request, swaps the form for the confirmation message and moves focus to it.
 * Server-side errors are announced in the status line and the form stays editable. A filled
 * honeypot field (`name="site"`) is treated as a bot: the request is skipped silently but the
 * confirmation is still shown.
 * @returns {void}
 */
function initDemoForm() {
    const form = document.querySelector('#form-demo'),
        status = form.querySelector('.contact-card--status'),
        button = form.querySelector('button[type="submit"]'),
        success = document.querySelector('#form-demo-success');

    const showError = message => {
        status.textContent = message;
        status.classList.add('is-error');
    };

    form.addEventListener('bouncerFormInvalid', event => {
        const errors = event.detail.errors;
        const onlyConsent = errors.length === 1 && errors[0].name === 'consentimento';
        showError(onlyConsent ? DEMO_CONSENT_MESSAGE : 'Confira os campos destacados.');
    });

    form.addEventListener('bouncerFormValid', () => {
        if (button.disabled) {
            return;
        }

        status.textContent = '';
        status.classList.remove('is-error');

        if (!form.elements.namedItem('consentimento').checked) {
            showError(DEMO_CONSENT_MESSAGE);
            return;
        }

        fillTrafficOrigin(form);
        const isBot = form.elements.namedItem('site').value !== '';
        const request = isBot ? Promise.resolve() : sendDemoRequest(form);

        button.disabled = true;
        form.setAttribute('aria-busy', 'true');
        status.textContent = 'Enviando…';

        request
            .then(() => {
                status.textContent = '';
                form.hidden = true;
                success.hidden = false;
                success.focus();
                success.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
                if (window.dataLayer) {
                    window.dataLayer.push({ event: 'lead_demonstracao' });
                }
            })
            .catch(error => {
                showError(error.message);
            })
            .finally(() => {
                button.disabled = false;
                form.removeAttribute('aria-busy');
            });
    });
}

if (document.querySelector('#form-demo') !== null) {
    initDemoForm();
}
